import type { TroubledSocialAccount } from "@brandex/api-client";
import { useBrandex } from "@brandex/dashboard";
import { formatRelative } from "@brandex/i18n";
import { Alert, Badge, Button, EmptyState, PlatformIcon, Spinner, type SocialMark } from "@brandex/ui";
import { useQuery } from "@tanstack/react-query";
import { CheckCircle2 } from "lucide-react";
import { useState } from "react";

import { can, useStaffAccess } from "../access";
import { QueryError } from "../views";
import { useManageableClients } from "./content";
import { SocialIntegrations } from "./social-integrations";

const NAMES: Record<string, string> = { youtube: "YouTube", facebook: "Facebook", instagram: "Instagram", tiktok: "TikTok", linkedin: "LinkedIn", google: "Google reviews" };
const MARKS = new Set<string>(["youtube", "facebook", "instagram", "tiktok", "linkedin", "google"]);

/** What staff should do about each problem code (ADR 0024). */
const ACTION: Record<string, string> = {
  auth: "The platform stopped accepting our access. Reconnect it (or send the doctor the guide).",
  not_found: "The account was deleted, renamed or unshared. Check it with the doctor, then reconnect.",
  rate_limited: "The platform asked us to slow down. Retried automatically.",
  not_configured: "BrandEx's keys for this platform are missing, turned off or refused. Fix them under Integrations.",
  unavailable: "The platform didn't answer. Retried automatically with backoff.",
};

type Tab = "accounts" | "integrations";

/**
 * Social health (ADR 0024, 0025): the accounts that need attention across
 * the clients this staff member manages and, for integration managers, the
 * provider apps with how to set each one up.
 */
export function SocialHealthPage() {
  const access = useStaffAccess();
  const accounts = can(access.data, "platform.clients.manage", { assigned: true });
  const integrations = can(access.data, "platform.integrations.manage");
  const initial: Tab = new URLSearchParams(window.location.search).get("tab") === "integrations" ? "integrations" : "accounts";
  const [picked, setPicked] = useState<Tab>(initial);
  const tab: Tab = !accounts ? "integrations" : !integrations ? "accounts" : picked;
  const go = (t: Tab) => {
    setPicked(t);
    const url = new URL(window.location.href);
    url.searchParams.set("tab", t);
    window.history.replaceState(null, "", url);
  };
  if (access.isPending) return <Spinner label="Loading" />;
  return (
    <div className="grid gap-6">
      <header className="max-w-2xl">
        <h1 className="font-display text-2xl font-bold">Social health</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          {tab === "accounts"
            ? "Connected social accounts that stopped syncing, across your clients. Websites keep showing the last good posts until they are fixed."
            : "BrandEx's app keys for each social platform. Clients can connect a platform once its keys are saved and working."}
        </p>
      </header>
      {accounts && integrations ? (
        <nav aria-label="Social health" className="flex gap-1 border-b border-border">
          {(["accounts", "integrations"] as const).map((t) => (
            <button
              key={t}
              type="button"
              aria-current={tab === t ? "page" : undefined}
              onClick={() => go(t)}
              className={`-mb-px border-b-2 px-3 py-2.5 text-sm font-medium ${tab === t ? "border-primary text-foreground" : "border-transparent text-muted-foreground hover:text-foreground"}`}
            >
              {t === "accounts" ? "Accounts" : "Integrations"}
            </button>
          ))}
        </nav>
      ) : null}
      {tab === "accounts" ? <TroubledAccounts /> : <SocialIntegrations />}
    </div>
  );
}

function TroubledAccounts() {
  const { api, nav } = useBrandex();
  const health = useQuery<TroubledSocialAccount[]>({
    queryKey: ["admin", "social", "health"],
    queryFn: async () => {
      const { data } = await api.http.GET("/v1/admin/social/health");
      if (data === undefined) throw new Error("Empty response");
      return data;
    },
  });
  const clients = useManageableClients();
  const name = (org: string) => {
    const row = clients.data?.find((c) => c.organization_id === org);
    return row?.doctor_name ?? row?.organization_name ?? org;
  };
  return (
    <div className="grid gap-6">
      {health.isPending ? <Spinner label="Loading" /> : null}
      {health.isError ? <QueryError error={health.error} /> : null}
      {health.isSuccess && health.data.length === 0 ? (
        <EmptyState icon={<CheckCircle2 className="h-8 w-8 text-mint-500" aria-hidden />} title="All accounts are syncing" description="Nothing needs attention right now." />
      ) : null}
      {health.isSuccess && health.data.length > 0 ? (
        <div className="overflow-x-auto rounded-[var(--radius-lg)] border border-border">
          <table className="w-full min-w-[44rem] text-sm">
            <thead className="bg-muted/60 text-left text-xs uppercase tracking-wide text-muted-foreground">
              <tr>
                <th className="px-4 py-2">Client</th>
                <th className="px-4 py-2">Platform</th>
                <th className="px-4 py-2">Problem</th>
                <th className="px-4 py-2">Since</th>
                <th className="px-4 py-2"><span className="sr-only">Action</span></th>
              </tr>
            </thead>
            <tbody>
              {health.data.map((a) => {
                const problem = a.sync.problem;
                const href = `/clients/${a.organization_id}/manage?tab=social&platform=${a.platform}`;
                return (
                  <tr key={`${a.organization_id}:${a.platform}`} className="border-t border-border align-top">
                    <td className="px-4 py-3 font-medium">{name(a.organization_id)}</td>
                    <td className="px-4 py-3">
                      <span className="inline-flex items-center gap-2">
                        {MARKS.has(a.platform) ? <PlatformIcon platform={a.platform as SocialMark} className="h-4 w-4" /> : null}
                        {NAMES[a.platform] ?? a.platform}
                      </span>
                      <span className="block text-xs text-muted-foreground">{a.sync.profile.handle ?? a.sync.profile.name}</span>
                    </td>
                    <td className="px-4 py-3">
                      <Badge tone="warning">{a.sync.status === "needs_reconnect" ? "Reconnect needed" : "Sync problem"}</Badge>
                      <p className="mt-1 max-w-sm text-muted-foreground">{ACTION[problem?.code ?? ""] ?? problem?.message}</p>
                    </td>
                    <td className="px-4 py-3 text-muted-foreground">{problem ? formatRelative("en", problem.at) : "—"}</td>
                    <td className="px-4 py-3 text-right">
                      <Button size="sm" variant="outline" onClick={() => nav.navigate(href)}>
                        Open
                      </Button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : null}
      {clients.isError ? <Alert tone="info">Client names couldn't be loaded; ids are shown instead.</Alert> : null}
    </div>
  );
}
